import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Input, Stack, Typography } from '@mui/joy';
import { payrollRequest } from '../../payrollApi';
import { FIELD_LABELS, dayLabel, money } from './payrollUtils';
import { payrollSurfaceSx, sectionSx } from './payrollStyles';

const PARAMS = { threshold: 'Порог выручки', base_reward: 'Базовая премия',
    per_step: 'Шаг выручки', step_reward: 'Премия за шаг', day_rate: 'Дневной оклад',
    night_rate: 'Ночной оклад', day_start: 'Начало дневного тарифа', day_end: 'Конец дневного тарифа',
    revenue_percent: 'Процент выручки' };
export function RulesView({ payload }) {
    return <Stack spacing={1}>{Object.entries(payload || {}).map(([key, value]) =>
        <Box key={key}><Typography level="body-sm" fontWeight="lg">{FIELD_LABELS[key] || key}</Typography>
            {typeof value === 'boolean' ? <Typography level="body-sm">{value ? 'Да' : 'Нет'}</Typography>
                : Array.isArray(value) ? (value.length ? value : [{ min: 0, max: null, percent: 0 }]).map((tier, index) => <Typography key={index} level="body-sm">
                    От {tier.min} ₽ до {tier.max === null ? '∞' : `${tier.max} ₽`}: {tier.percent}%
                </Typography>) : Object.entries(value || {}).length
                    ? Object.entries(value).map(([name, item]) => <Typography key={name} level="body-sm">
                        {PARAMS[name] || name}: {String(item)}{name === 'revenue_percent' ? '%' : name.includes('start') || name.includes('end') ? '' : ' ₽'}
                    </Typography>) : <Typography level="body-sm">Не настроено</Typography>}
        </Box>)}</Stack>;
}

export default function PayrollHistory({ token, configId, refresh }) {
    const [data, setData] = useState(null), [error, setError] = useState('');
    const [busy, setBusy] = useState(false), [date, setDate] = useState('');
    const [dated, setDated] = useState(null), [attempt, setAttempt] = useState(0);
    const generation = useRef(0);
    useEffect(() => {
        const id = ++generation.current;
        setData(null); setDated(null); setError(''); setBusy(false); setDate('');
        if (!configId) return undefined;
        const controller = new AbortController();
        payrollRequest(token, `finance/configs/${configId}/history/`, { signal: controller.signal })
            .then(value => { if (id === generation.current) setData(value); })
            .catch(err => { if (!controller.signal.aborted && id === generation.current) setError(err.message); });
        return () => { controller.abort(); generation.current++; };
    }, [token, configId, refresh, attempt]);
    async function more() {
        if (busy) return;
        const id = generation.current;
        setBusy(true); setError('');
        try {
            const value = await payrollRequest(token, `finance/configs/${configId}/history/?before_id=${data.next_before_id}`);
            if (id === generation.current) setData(old => ({ ...value, audit: [...old.audit, ...value.audit] }));
        } catch (err) { if (id === generation.current) setError(err.message); }
        finally { if (id === generation.current) setBusy(false); }
    }
    async function readDate() {
        if (!date || busy) return;
        const id = generation.current;
        setBusy(true); setError(''); setDated(null);
        try {
            const value = await payrollRequest(token, `finance/configs/${configId}/?date=${encodeURIComponent(date)}`);
            if (id === generation.current) setDated({ date, value });
        } catch (err) { if (id === generation.current) setError(err.message); }
        finally { if (id === generation.current) setBusy(false); }
    }
    if (!configId) return null;
    return <Box component="details" sx={{ ...payrollSurfaceSx, ...sectionSx }}>
        <Typography component="summary" fontWeight="lg">История условий и изменений</Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
            {error && <Alert color="danger">{error}<Button size="sm" onClick={() => setAttempt(v => v + 1)}>Повторить</Button></Alert>}
            {!data && !error && <Typography>Загрузка истории…</Typography>}
            {data?.versions?.map(([day, payload]) => <Box component="details" key={day}>
                <Typography component="summary">С {dayLabel(day)}</Typography><RulesView payload={payload} />
            </Box>)}
            <Typography level="body-sm">Посмотреть условия на дату (без изменения формы):</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><Input type="date" value={date} disabled={busy}
                onChange={event => { setDate(event.target.value); setDated(null); }} />
                <Button onClick={readDate} loading={busy} disabled={!date}>Показать</Button></Stack>
            {dated && <Box><Typography>Условия на {dated.date}</Typography>
                <RulesView payload={Object.fromEntries(Object.keys(FIELD_LABELS).map(key => [key, dated.value[key]]))} /></Box>}
            <Typography fontWeight="lg">Журнал применения</Typography>
            {data?.audit?.length === 0 && <Typography level="body-sm">Применений пока нет.</Typography>}
            {data?.audit?.map(item => <Box component="details" key={item.id}>
                <Typography component="summary">{item.created_at} · {item.actor} · разница {money(item.summary.delta)}</Typography>
                <Typography level="body-sm">С {dayLabel(item.change.start)}; дней: {item.summary.days}</Typography>
                {item.change.operation === 'binding' && <Typography level="body-sm">Назначение точки №{item.change.location_id}: {item.change.config_id === null ? 'автоматика отключена' : `конфигурация №${item.change.config_id}`}</Typography>}
                <RulesView payload={item.change.patch} />
                <Typography level="body-sm">До: {money(item.summary.before_total)}. После: {money(item.summary.after_total)}.</Typography>
            </Box>)}
            {data?.next_before_id && <Button onClick={more} loading={busy}>Ещё записи</Button>}
        </Stack>
    </Box>;
}